import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useTheme } from "@mui/material/styles";
import { listApprovals } from "../../api/agentApi";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../../constants";

interface ApprovalSummary {
  id: string;
  workflow_run_id: string;
  step_id: string;
  title: string;
  prompt: string;
  status: string;
  created_at: string | null;
  expires_at: string | null;
  responded_at: string | null;
  responded_by: string | null;
}

const BORDER_SOFT = "#E5D4C4";
const MUTED = "#6F6A64";
const TABLE_HEADER_BG = "#F7F2ED";

const primaryActionButtonSx = {
  borderRadius: UI_RADIUS.button,
  bgcolor: "primary.main",
  boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
  color: "primary.contrastText",
  textTransform: "none",
  fontWeight: 900,
  minHeight: 36,
  px: 2,
  "&:hover": {
    bgcolor: "primary.dark",
    boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}`,
  },
};

const secondaryActionButtonSx = {
  borderRadius: UI_RADIUS.button,
  borderColor: BORDER_SOFT,
  color: BRAND_COLORS.primary.dark,
  textTransform: "none",
  fontWeight: 850,
  minHeight: 40,
  px: 2,
  "&:hover": {
    borderColor: BRAND_COLORS.primary.main,
    bgcolor: BRAND_COLORS.primary.softSubtle,
  },
};

const tableHeaderCellSx = {
  bgcolor: TABLE_HEADER_BG,
  fontWeight: 900,
  py: 1.5,
};

export default function ApprovalsListPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [approvals, setApprovals] = useState<ApprovalSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>("all");

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statusFilter = selectedTab === "all" ? null : selectedTab;
      const data = await listApprovals(statusFilter);
      setApprovals(data.approvals || []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [selectedTab]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const getStatusColor = (status: string, expiresAt: string | null) => {
    if (isExpired(expiresAt)) return "error";
    switch (status) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const getEmptyMessage = () => {
    switch (selectedTab) {
      case "pending":
        return {
          title: "No pending approvals",
          subtitle:
            "When a workflow reaches a human input step, approval requests will appear here.",
        };
      case "approved":
        return {
          title: "No approved requests",
          subtitle: "Approved approval requests will appear here.",
        };
      case "rejected":
        return {
          title: "No rejected requests",
          subtitle: "Rejected approval requests will appear here.",
        };
      case "expired":
        return {
          title: "No expired requests",
          subtitle: "Expired approval requests will appear here.",
        };
      default:
        return {
          title: "No approvals",
          subtitle:
            "When a workflow reaches a human input step, approval requests will appear here.",
        };
    }
  };
  const panelBg = isDark ? "#181411" : "#FFFEFC";
  const panelSoftBg = isDark ? "rgba(255,247,239,0.06)" : TABLE_HEADER_BG;
  const borderSoft = isDark ? "rgba(224,177,132,0.24)" : BORDER_SOFT;
  const mutedText = isDark ? "text.secondary" : MUTED;
  const copperText = isDark ? "#F2C69B" : BRAND_COLORS.primary.dark;
  const secondaryActionThemedSx = {
    ...secondaryActionButtonSx,
    borderColor: borderSoft,
    color: copperText,
    "&:hover": {
      borderColor: isDark ? "rgba(224,177,132,0.42)" : BRAND_COLORS.primary.main,
      bgcolor: isDark ? "rgba(224,177,132,0.12)" : BRAND_COLORS.primary.softSubtle,
    },
  };
  const tableHeaderCellThemedSx = {
    ...tableHeaderCellSx,
    bgcolor: panelSoftBg,
    color: "text.primary",
  };

  return (
    <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box>
          <Typography variant="h3" component="h1" sx={PAGE_TYPOGRAPHY.heading}>
            Approvals
          </Typography>
          <Typography variant="body2" sx={{ color: mutedText, mt: 0.35 }}>
            Human-in-the-loop approval requests for your workflows
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ pt: 0.2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchApprovals}
            disabled={loading}
            sx={secondaryActionThemedSx}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Status Tabs */}
      <Paper
        elevation={0}
        sx={{
          mb: PAGE_SHELL.sectionGap,
          borderRadius: UI_RADIUS.panel,
          border: `1px solid ${borderSoft}`,
          bgcolor: panelBg,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          aria-label="approval status tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            minHeight: 44,
            "& .MuiTabs-indicator": { bgcolor: "primary.main" },
            "& .MuiTab-root": {
              minHeight: 44,
              color: mutedText,
              fontWeight: 800,
            },
            "& .Mui-selected": { color: `${copperText} !important` },
          }}
        >
          <Tab label="All" value="all" sx={{ textTransform: "none" }} />
          <Tab label="Pending" value="pending" sx={{ textTransform: "none" }} />
          <Tab label="Approved" value="approved" sx={{ textTransform: "none" }} />
          <Tab label="Rejected" value="rejected" sx={{ textTransform: "none" }} />
          <Tab label="Expired" value="expired" sx={{ textTransform: "none" }} />
        </Tabs>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${borderSoft}`,
          borderRadius: UI_RADIUS.panel,
          bgcolor: panelBg,
          overflow: "hidden",
        }}
      >
        {loading && approvals.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : approvals.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              {getEmptyMessage().title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {getEmptyMessage().subtitle}
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeaderCellThemedSx}>
                  Title
                </TableCell>
                <TableCell sx={tableHeaderCellThemedSx}>
                  Step
                </TableCell>
                <TableCell sx={tableHeaderCellThemedSx}>
                  Status
                </TableCell>
                <TableCell sx={tableHeaderCellThemedSx}>
                  Created
                </TableCell>
                <TableCell sx={tableHeaderCellThemedSx}>
                  Expires
                </TableCell>
                <TableCell sx={tableHeaderCellThemedSx}>
                  Responded At
                </TableCell>
                <TableCell sx={tableHeaderCellThemedSx}>
                  Responded By
                </TableCell>
                <TableCell align="center" sx={tableHeaderCellThemedSx}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {approvals.map((approval) => {
                const expired = isExpired(approval.expires_at);
                const isPending = approval.status === "pending" && !expired;
                return (
                  <TableRow
                    key={approval.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(`/workflow/approvals/${approval.id}`)
                    }
                  >
                    <TableCell>
                      <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                        {approval.title || "Approval Required"}
                      </Typography>
                      {approval.prompt && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: "12px",
                            mt: 0.25,
                            maxWidth: 300,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {approval.prompt}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "12px",
                          color: mutedText,
                        }}
                      >
                        {approval.step_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={expired ? "expired" : approval.status}
                        size="small"
                        color={getStatusColor(approval.status, approval.expires_at)}
                        variant="outlined"
                        sx={{ borderRadius: UI_RADIUS.chip, fontSize: "11px", fontWeight: 800 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", color: mutedText }}>
                      {approval.created_at
                        ? new Date(approval.created_at).toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", color: mutedText }}>
                      {approval.expires_at
                        ? new Date(approval.expires_at).toLocaleString()
                        : "No expiry"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", color: mutedText }}>
                      {approval.responded_at
                        ? new Date(approval.responded_at).toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", color: mutedText }}>
                      {approval.responded_by || "—"}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/workflow/approvals/${approval.id}`);
                        }}
                        sx={{ ...primaryActionButtonSx, minHeight: 30, fontSize: "12px", px: 2 }}
                      >
                        {isPending ? "Review" : "View"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
